import { Link, useLocation } from 'react-router-dom';
import { useRef, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  LayoutDashboard, Package, DollarSign, Receipt, LogOut, Sun, Moon,
  ListOrdered, Calculator, Wallet, Download, Upload, ScanLine, Ship
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: '/calculator', label: 'Calculator', icon: Calculator },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/inventory', label: 'Inventory', icon: Package },
  { path: '/sales', label: 'Sales', icon: DollarSign },
  { path: '/expenses', label: 'Expenses', icon: Receipt },
  { path: '/shipments', label: 'Shipments', icon: Ship },
  { path: '/pricesheet', label: 'Price Sheet', icon: ListOrdered },
  { path: '/my-prices', label: 'My Prices', icon: Wallet },
  { path: '/imei', label: 'IMEI Check', icon: ScanLine },
];

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/83824f35-3ffa-4a4b-99df-eb320d1f49ec/images/f7e8960f40cd8a0232b0979111e2b7626e4de012be8e45f7731b894971bd151a.png";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const importInputRef = useRef(null);
  const [lastExportAt, setLastExportAt] = useState(undefined); // undefined=loading, null=never

  const fetchBackupStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/export/status`, { withCredentials: true });
      setLastExportAt(res.data?.last_export_at || null);
    } catch {
      setLastExportAt(null);
    }
  }, []);

  useEffect(() => { fetchBackupStatus(); }, [fetchBackupStatus]);

  // Auto-backup: on load + every 5 minutes + on window close. The backend
  // snapshots ALL user data into `user_backups` and stamps last_export_at.
  useEffect(() => {
    let cancelled = false;
    const runAutoBackup = async () => {
      try {
        await axios.post(`${API}/backup/auto`, {}, { withCredentials: true });
        if (!cancelled) fetchBackupStatus();
      } catch (_e) { /* ignore — silent auto-backup */ }
    };
    // First one 30s after mount so the initial page loads settle first
    const first = setTimeout(runAutoBackup, 30 * 1000);
    const interval = setInterval(runAutoBackup, 5 * 60 * 1000);
    // Best-effort backup when the user leaves the page
    const onLeave = () => {
      try {
        // keepalive lets the request survive page unload
        fetch(`${API}/backup/auto`, {
          method: 'POST', credentials: 'include', keepalive: true,
        });
      } catch (_e) { /* ignore */ }
    };
    window.addEventListener('beforeunload', onLeave);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(interval);
      window.removeEventListener('beforeunload', onLeave);
    };
  }, [fetchBackupStatus]);

  const backupDaysAgo = lastExportAt
    ? Math.floor((Date.now() - new Date(lastExportAt).getTime()) / 86400000)
    : null;
  const backupOverdue = lastExportAt === null || (backupDaysAgo != null && backupDaysAgo >= 7);
  const fmtBackup = () => {
    if (lastExportAt === undefined) return '…';
    if (lastExportAt === null) return 'never';
    const diff = (Date.now() - new Date(lastExportAt).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${backupDaysAgo}d ago`;
  };

  const handleExportData = async () => {
    try {
      toast.loading('Preparing your backup…', { id: 'export-toast' });
      const res = await axios.get(`${API}/export/all`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fliptrack-backup-${today}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Backup downloaded', { id: 'export-toast' });
      fetchBackupStatus();
    } catch (err) {
      toast.error(err.response?.status === 401 ? 'Please sign in again' : 'Export failed', { id: 'export-toast' });
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = window.confirm(
      'Import will REPLACE all data in THIS account with the backup file. Continue?'
    );
    if (!ok) {
      if (importInputRef.current) importInputRef.current.value = '';
      return;
    }
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API}/import/all`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${res.data.total} records · reloading…`);
      setTimeout(() => window.location.reload(), 1300);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden" data-testid="app-layout">
      <input
        type="file" accept=".json,application/json" ref={importInputRef}
        className="hidden" onChange={handleImportFile} data-testid="import-data-input"
      />
      {/* Top bar: brand + theme + profile */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-link">
              <img src={LOGO_URL} alt="FlipTrack" className="h-9 w-9 rounded-lg" />
              <span className="font-display text-2xl text-foreground tracking-wider hidden sm:inline">FLIPTRACK</span>
            </Link>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <span className="relative">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.picture} />
                        <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                          {user?.name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      {backupOverdue && (
                        <span
                          className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background"
                          title="Backup recommended"
                          data-testid="backup-overdue-dot"
                        />
                      )}
                    </span>
                    <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-muted-foreground">Last backup</span>
                    <span
                      className={`text-[11px] font-medium ${backupOverdue ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground'}`}
                      data-testid="last-backup-label"
                    >
                      {fmtBackup()}
                    </span>
                  </div>
                  <DropdownMenuItem onClick={handleExportData} data-testid="export-data-btn" className="cursor-pointer">
                    <Download className="h-4 w-4 mr-2" /> Export All Data
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => importInputRef.current?.click()} data-testid="import-data-btn">
                    <Upload className="h-4 w-4 mr-2" /> Import Data
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
                    <LogOut className="h-4 w-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Page tabs (like Google Sheets bottom tabs) */}
          <nav className="-mb-px overflow-x-auto" data-testid="top-nav">
            <ul className="flex items-end gap-1 min-w-max">
              {navItems.map(item => {
                const active = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                        active
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      <item.icon className="h-4 w-4" strokeWidth={2} />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          {children}
        </div>
      </main>
    </div>
  );
}
